'use client';

import { useMemo } from 'react';

import type { ComboboxOptionType, ComboboxOptionsType } from '../types/combobox-root.types';
import { isGroup } from '../utils/is-group';

/**
 * Hook that creates a Map of option values to their corresponding option objects
 * for efficient lookup in a combobox component
 *
 * @param options - Array of combobox options (can include strings, option objects, and option groups)
 * @returns A Map with option values as keys and option objects as values
 */
export const useComboboxOptionsMap = (options: ComboboxOptionsType) => {
  return useMemo(() => {
    const map = new Map<string, ComboboxOptionType>();

    // Process each option or group
    options.forEach((option) => {
      if (typeof option === 'string') {
        // Handle string options
        map.set(option, option);
      } else if (isGroup(option)) {
        // Handle option groups
        option.options.forEach((innerOption) => {
          if (typeof innerOption === 'string') {
            map.set(innerOption, innerOption);
          } else {
            map.set(innerOption.value, innerOption);
          }
        });
      } else {
        // Handle option objects
        map.set(option.value, option);
      }
    });

    return map;
  }, [options]);
};
